'use client';
import type React from 'react';
import { useState, useEffect, useRef } from 'react';
import { usePathname } from 'next/navigation';
import Link from 'next/link';
import Image from 'next/image';

import { cn } from '@skybeam/shared-utils/classnames/cn';
import { Button } from '@simulmedia/ui-kit';
import { NavbarMobile } from './navbar-mobile';
import { NavItem } from './nav-item';
import Logo from '../../../../public/skybeam-logo-with-text-blue.svg';
import MenuSVG from '../../../../public/menu.svg';

import {
  Video,
  ShoppingBag,
  HandPlatter,
  House,
  BriefcaseBusiness,
  CircleSmall,
  MapPinHouse,
  ChevronDown,
  HeartPulse,
} from 'lucide-react';

import styles from './navbar.module.css';
import { useSessionStorage } from 'usehooks-ts';
import { SESSION_KEY, UTMCapture } from '../utm-capture/utm-capture';

export type SubItem = {
  featuredSrc?: string;
  featuredTitle?: string;
  icon: React.ReactNode;
  label: string;
  link: string;
};

export type TopNavItem = {
  label: string;
  link?: string;
  subItems?: SubItem[];
};

export type NavbarModeType =
  | 'light'
  | 'dark'
  | 'white'
  | 'amber'
  | 'transparent';

const industriesSubItems: SubItem[] = [
  {
    link: '/industry/agencies',
    label: 'Agencies',
    icon: (
      <Video className="desktop:size-6 size-4 text-white transition-all duration-300 group-hover:fill-blue-600 group-hover:text-blue-600" />
    ),
    featuredSrc: '/agencies-bg.jpg',
    featuredTitle: 'CTV Advertising Platform Built For Agencies',
  },
  {
    link: '/industry/franchisees',
    label: 'Franchisees',
    icon: (
      <ShoppingBag className="desktop:size-6 size-4 text-white transition-all duration-300 group-hover:fill-blue-600 group-hover:text-blue-900" />
    ),
    featuredSrc: '/franchises-bg.png',
    featuredTitle: 'TV Advertising Built for Franchise Growth',
  },
  {
    link: '/industry/smb',
    label: 'SMBs',
    icon: (
      <BriefcaseBusiness className="desktop:size-6 size-4 text-white transition-all duration-300 group-hover:fill-blue-600 group-hover:text-blue-900" />
    ),
    featuredSrc: '/smb-bg.png',
    featuredTitle: 'TV Advertising Made Easy for SMBs & Local Businesses',
  },
  {
    link: '/industry/healthcare',
    label: 'Healthcare',
    icon: (
      <HeartPulse className="desktop:size-6 size-4 text-white transition-all duration-300 group-hover:fill-blue-600 group-hover:text-blue-900" />
    ),
    featuredSrc: '/healthcare-bg.png',
    featuredTitle: 'TV Advertising Platform for Healthcare Providers',
  },
  {
    link: '',
    label: 'Restaurants',
    icon: (
      <HandPlatter className="desktop:size-6 size-4 text-white transition-all duration-300 group-hover:fill-blue-600 group-hover:text-blue-600" />
    ),
  },
  {
    link: '',
    label: 'Home Services',
    icon: (
      <House className="desktop:size-6 size-4 text-white transition-all duration-300 group-hover:fill-blue-600 group-hover:text-blue-600" />
    ),
  },
];

const productSubItems: SubItem[] = [
  {
    link: '/product/local-tv-advertising',
    label: 'Local TV',
    icon: (
      <MapPinHouse className="desktop:size-6 size-4 text-white transition-all duration-300 group-hover:fill-blue-600 group-hover:text-blue-900" />
    ),
    featuredSrc: '/girl-watching-tv-illustration.png',
    featuredTitle: 'Local TV',
  },
];

const navItems: TopNavItem[] = [
  { label: 'Product', subItems: productSubItems },
  { label: 'Industries', subItems: industriesSubItems },
  { link: '/blog', label: 'Blog' },
  { link: '/pricing', label: 'Pricing' },
  { link: '/about', label: 'About Us' },
];

export const Navbar: React.FC = () => {
  const pathname = usePathname();
  const dashboardUrl = process.env.NEXT_PUBLIC_DASHBOARD_URL;

  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const getInitialNavbarMode = () => {
    if (pathname === '/') {
      return 'transparent';
    }
    if (pathname.startsWith('/blog/')) {
      return 'white';
    }
    if (pathname === '/product/local-tv-advertising') {
      return 'amber';
    }
    if (pathname !== '/' && pathname !== '/blog' && pathname !== '/pricing') {
      return 'light';
    }
    return 'dark';
  };

  const [utmValues] = useSessionStorage<string>(SESSION_KEY, '');

  const signUpUrl = utmValues
    ? `${dashboardUrl}/sign-up?${utmValues}`
    : `${dashboardUrl}/sign-up`;

  const [isNavbarMobileOpen, setIsNavbarMobileOpen] = useState(false);
  const [navbarMode, setNavbarMode] =
    useState<NavbarModeType>(getInitialNavbarMode);

  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [featuredIndex, setFeaturedIndex] = useState<number>(0);

  const firstFeaturedIndex = (items: SubItem[] = []) => {
    const idx = items.findIndex((it) => it.featuredSrc && it.featuredTitle);
    return idx >= 0 ? idx : 0;
  };

  const closeTimer = useRef<NodeJS.Timeout | null>(null);
  const previousNavbarModeRef = useRef<NavbarModeType | null>(null);
  const navbarModeRef = useRef<NavbarModeType>(navbarMode);

  const currentSubItems =
    openIndex !== null ? navItems[openIndex].subItems : undefined;

  const openMenu = (index: number) => {
    if (!navItems[index].subItems) return;
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
    setOpenIndex(index);
    setFeaturedIndex(firstFeaturedIndex(navItems[index].subItems!));
  };

  const scheduleClose = (time = 100) => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
    }
    closeTimer.current = setTimeout(() => setOpenIndex(null), time);
  };

  const handleOpenMobileMenu = () => {
    setIsNavbarMobileOpen(true);
  };

  const handleCloseMobileMenu = () => {
    setIsNavbarMobileOpen(false);
  };

  useEffect(() => {
    let ticking = false;

    const handleScroll = () => {
      if (!ticking) {
        ticking = true;
        window.requestAnimationFrame(() => {
          if (openIndex !== null) {
            ticking = false;
            return;
          }

          const sections = document.querySelectorAll('[data-section]');
          let currentSection: any = null;

          sections.forEach((section) => {
            const rect = section.getBoundingClientRect();
            if (rect.top <= 80 && rect.bottom >= 80) {
              currentSection = section as HTMLElement;
            }
          });

          if (currentSection) {
            const sectionId = currentSection.getAttribute('data-section');

            switch (sectionId) {
              case 'heading-section': {
                const scrollY = window.scrollY || window.pageYOffset;
                if (scrollY <= 50) {
                  setNavbarMode('transparent');
                } else {
                  setNavbarMode('light');
                }
                break;
              }
              case 'faq-section':
              case 'resources-section':
              case 'pricing-heading-section':
              case 'product-run-local-campaigns-section':
                setNavbarMode('dark');
                break;
              case 'article-section':
              case 'product-local-tv-business-few-clicks-section':
              case 'main-digital-campaign-section':
              case 'powered-by-simulmedia-section':
              case 'contact-section':
              case 'industry-cards-section':
              case 'launch-campaign-section':
              case 'powered-by-section':
              case 'home-partners-section':
              case 'get-seen-section':
              case 'testimonial-section':
              case 'latest-resources-section-light':
                setNavbarMode('white');
                break;
              case 'product-local-tv-advertising-page':
              case 'main-features-section':
                setNavbarMode('amber');
                break;
              default:
                setNavbarMode('light');
                break;
            }
          }
          ticking = false;
        });
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    handleScroll();

    return () => window.removeEventListener('scroll', handleScroll);
  }, [openIndex]);

  useEffect(() => {
    navbarModeRef.current = navbarMode;
  }, [navbarMode]);

  useEffect(() => {
    if (openIndex !== null) {
      if (navbarModeRef.current === 'transparent') {
        previousNavbarModeRef.current = 'transparent';
        setNavbarMode('light');
      }
    } else if (previousNavbarModeRef.current === 'transparent') {
      const scrollY = window.scrollY || window.pageYOffset;
      const sections = document.querySelectorAll('[data-section]');
      let currentSection: any = null;

      sections.forEach((section) => {
        const rect = section.getBoundingClientRect();
        if (rect.top <= 80 && rect.bottom >= 80) {
          currentSection = section as HTMLElement;
        }
      });

      if (
        currentSection?.getAttribute('data-section') === 'heading-section' &&
        scrollY <= 50
      ) {
        setNavbarMode('transparent');
      }
      previousNavbarModeRef.current = null;
    }
  }, [openIndex]);

  if (isNavbarMobileOpen) {
    return (
      <NavbarMobile
        navItems={navItems}
        navbarMode={navbarMode}
        setClose={handleCloseMobileMenu}
      />
    );
  }

  return (
    <div
      data-test="marketing-header"
      className={cn(
        styles.navbar,
        styles[navbarMode],
        'tablet:py-5 relative z-100 flex w-full justify-center border-t border-b border-transparent py-2 font-sans transition-colors duration-400',
      )}
      onMouseEnter={() =>
        closeTimer.current && clearTimeout(closeTimer.current!)
      }
    >
      <div className="tablet:px-6 laptop:px-10 desktop:px-0 desktop:max-w-284 flex w-full max-w-full items-center justify-between px-0">
        <div className="flex">
          <MenuSVG
            className={cn(
              'tablet:mr-8 desktop:hidden mr-4',
              navbarMode === 'white' || navbarMode === 'amber'
                ? 'text-[#0A0A0A]'
                : 'text-white',
            )}
            onClick={handleOpenMobileMenu}
          />
          <Link data-test="navbar-logo-link" href="/">
            <Logo
              id="skybeam-logo"
              className={cn('tablet:size-full h-8 w-[99px]', styles.logo)}
              viewBox="0 0 127 32"
            />
          </Link>
          <div className="desktop:flex hidden items-center pl-10">
            {navItems.map(({ label, link, subItems }, i) => {
              if (!subItems && link) {
                return (
                  <NavItem
                    key={link}
                    href={link}
                    className="px-4 py-1"
                    isActive={pathname === link && pathname !== '/'}
                  >
                    {label}
                  </NavItem>
                );
              }
              return (
                <div
                  key={label}
                  data-test={
                    label === 'Product'
                      ? 'navbar-product-item'
                      : label === 'Industries'
                        ? 'navbar-industries-item'
                        : undefined
                  }
                  onMouseEnter={() => openMenu(i)}
                  onMouseLeave={() => scheduleClose(200)}
                >
                  <div
                    data-test={
                      label === 'Product'
                        ? 'navbar-product-trigger'
                        : label === 'Industries'
                          ? 'navbar-industries-trigger'
                          : undefined
                    }
                    className="flex items-center gap-1 px-4 py-1 text-base font-semibold"
                  >
                    {label}
                    <ChevronDown
                      className={cn(
                        styles.arrowDown,
                        'size-4 transition-transform',
                        openIndex === i && 'rotate-180',
                      )}
                    />
                  </div>

                  {openIndex === i && (
                    <div
                      data-test={
                        label === 'Product'
                          ? 'navbar-product-menu'
                          : label === 'Industries'
                            ? 'navbar-industries-menu'
                            : undefined
                      }
                      className={cn(
                        'absolute top-[78px] left-0 flex w-full justify-center pt-5 pb-16 transition-colors duration-500',
                        navbarMode === 'transparent'
                          ? 'bg-blue-800'
                          : styles[navbarMode],
                      )}
                    >
                      <div className="flex w-full max-w-284 items-start gap-x-1">
                        <div className="grid w-full grid-flow-col grid-rows-3 gap-x-2">
                          {subItems?.map(({ icon, label, link }, index) => {
                            if (link) {
                              return (
                                <Link
                                  key={label}
                                  data-test={
                                    link === '/product/local-tv-advertising'
                                      ? 'navbar-product-local-tv-button'
                                      : link === '/industry/agencies'
                                        ? 'navbar-industries-agencies-button'
                                        : link === '/industry/franchisees'
                                          ? 'navbar-industries-franchisees-button'
                                          : link === '/industry/smb'
                                            ? 'navbar-industries-smbs-button'
                                            : link === '/industry/healthcare'
                                              ? 'navbar-industries-healthcare-button'
                                              : undefined
                                  }
                                  className="desktop:max-w-[269px] self-start"
                                  href={link}
                                  onClick={() => setOpenIndex(null)}
                                >
                                  <div
                                    className="group flex h-full items-center gap-x-5 rounded-sm p-4 transition-all duration-300 hover:bg-white hover:text-black"
                                    onMouseEnter={() => {
                                      if (
                                        subItems?.[index]?.featuredSrc &&
                                        subItems?.[index]?.featuredTitle
                                      ) {
                                        setFeaturedIndex(index);
                                      }
                                    }}
                                  >
                                    <div
                                      data-test={
                                        link === '/product/local-tv-advertising'
                                          ? 'navbar-product-local-tv-icon'
                                          : undefined
                                      }
                                      className="flex size-12 items-center justify-center rounded-sm border border-blue-500 bg-blue-700 group-hover:border-blue-200 group-hover:bg-blue-100"
                                    >
                                      {icon}
                                    </div>
                                    <span className="text-base font-semibold">
                                      {label}
                                    </span>
                                  </div>
                                </Link>
                              );
                            }
                            return (
                              <div
                                key={label}
                                data-test={
                                  label === 'Restaurants'
                                    ? 'navbar-industries-restaurants-button'
                                    : label === 'Home Services'
                                      ? 'navbar-industries-home-services-button'
                                      : undefined
                                }
                                className="flex items-center gap-x-5 p-4"
                              >
                                <div className="flex size-12 items-center justify-center rounded-sm border border-blue-500 bg-blue-700">
                                  {icon}
                                </div>
                                <span className="text-base font-semibold">
                                  {label}
                                </span>
                                <div
                                  data-test={
                                    label === 'Restaurants'
                                      ? 'navbar-industries-restaurants-coming-soon'
                                      : label === 'Home Services'
                                        ? 'navbar-industries-home-services-coming-soon'
                                        : undefined
                                  }
                                  className={cn(
                                    'flex items-center gap-x-0.5 rounded-md py-0.5 pr-2 pl-1 text-sm',
                                    navbarMode === 'light'
                                      ? 'bg-blue-800'
                                      : navbarMode === 'dark'
                                        ? 'bg-blue-950'
                                        : 'bg-blue-100',
                                  )}
                                >
                                  <CircleSmall
                                    className={cn(
                                      'size-3',
                                      navbarMode === 'white' ||
                                        navbarMode === 'amber' ||
                                        navbarMode === 'transparent'
                                        ? 'fill-lime-500 text-lime-500'
                                        : 'fill-lime-200 text-lime-200',
                                    )}
                                  />
                                  <span
                                    className={cn(
                                      'text-xs text-white',
                                      navbarMode === 'white' ||
                                        navbarMode === 'amber' ||
                                        navbarMode === 'transparent'
                                        ? 'text-blue-700'
                                        : '',
                                    )}
                                  >
                                    Coming soon
                                  </span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        <div className="w-full max-w-[456px]">
                          <div>
                            <Image
                              data-test={
                                currentSubItems?.[featuredIndex].link ===
                                '/product/local-tv-advertising'
                                  ? 'navbar-product-local-tv-image'
                                  : undefined
                              }
                              src={
                                currentSubItems?.[featuredIndex].featuredSrc ||
                                ''
                              }
                              alt={
                                currentSubItems?.[featuredIndex]
                                  .featuredTitle || ''
                              }
                              width={456}
                              height={222}
                              className="h-[222px] max-h-[222px] max-w-[456px] rounded-lg object-cover object-top"
                            />
                            <div
                              className={cn(
                                navbarMode === 'light' ||
                                  navbarMode === 'transparent'
                                  ? 'bg-white text-blue-700'
                                  : 'bg-blue-700 text-white',
                                '-mt-3.5 rounded-xl px-5 pt-8 pb-5',
                              )}
                            >
                              <Link
                                data-test={
                                  currentSubItems?.[featuredIndex].link ===
                                  '/product/local-tv-advertising'
                                    ? 'navbar-product-local-tv-featured-link'
                                    : undefined
                                }
                                href={
                                  currentSubItems?.[featuredIndex].link || '/'
                                }
                                onClick={() => setOpenIndex(null)}
                              >
                                {currentSubItems?.[featuredIndex].featuredTitle}
                              </Link>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
        <div className="tablet:flex hidden items-center gap-x-2">
          <NavItem
            href={`${process.env.NEXT_PUBLIC_CALENDLY_URL}`}
            className={styles.navButton}
            rel="noopener noreferrer"
          >
            <Button
              data-test="Book a Demo-btn"
              className="h-10 px-6 text-base font-semibold"
              variant="ghost"
            >
              Book a Demo
            </Button>
          </NavItem>
          {isHydrated && (
            <NavItem href={signUpUrl}>
              <Button
                data-test="Start Now-btn"
                className={cn(
                  'h-10 rounded-lg px-6 text-base font-semibold',
                  navbarMode === 'dark' ? 'text-white' : 'text-black',
                )}
                variant={navbarMode === 'dark' ? 'primary' : 'secondary'}
              >
                Start Now
              </Button>
            </NavItem>
          )}
        </div>
      </div>

      <UTMCapture />
    </div>
  );
};
